import { useEffect } from "react";

import { EditorContent as ReadOnlyEditorContent } from "@tiptap/react";

import { LANG } from "@/constants/Language";
import {
  Assignment,
  EmptyAssignment,
} from "@/types/models/Assignment.interface";
import { CheckForUnderstandingQuizAttachment } from "@/types/models/CheckForUnderstandingQuiz.interface";
import getChecksForUnderstandingQuizAttachment from "@/view/utils/getChecksForUnderstandingQuizAttachment";
import useAnnotationsState from "@/view/utils/useAnnotationsState";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import useAttachmentsContext from "@/view/utils/useAttachmentsContext";
import { IContent } from "@/view/utils/useSetInitialLevel";
import TextSelectionMenu from "./components/TextSelectionMenu";
import { insertChecksForUnderstanding } from "./extensions/ChecksForUnderstanding/utils/utils";
import useDataTransactions from "./hooks/useDataTransactions";
import useEditor from "./hooks/useEditor";
import { INode } from "./TiptapArticleBody.interface";
import { addXpathToNodes } from "./utils/utils";

export interface TiptapArticleBodyProps {
  currentFontSize: string;
  media: object;
  levelId: string;
  rawText: INode | null;
  legacyArticleId?: number | undefined;
  isPreview: boolean;
  toggleVocabularySupport?: ((value: boolean) => void) | undefined;
  keyTermDataContentId?: string | undefined;
  content: IContent;
  language: LANG;
  lexile?: string | undefined;
}

export const TiptapArticleBody = ({
  currentFontSize,
  media,
  levelId,
  rawText,
  legacyArticleId,
  isPreview,
  keyTermDataContentId,
  toggleVocabularySupport,
  content,
  language,
  lexile,
}: TiptapArticleBodyProps) => {
  const { updatedAssignment: assignment } = useAssignmentReducerContext() as {
    updatedAssignment: Assignment | EmptyAssignment;
  };
  const assignmentId: string | undefined = (assignment as Assignment)
    ?.content_id;

  const { focusAnnotationCard } = useAnnotationsState();
  const { attachments } = useAttachmentsContext();
  const checksForUnderstandingQuiz = getChecksForUnderstandingQuizAttachment(
    attachments,
  ) as CheckForUnderstandingQuizAttachment;

  // Adding XPath to all nodes to enable Listen Mode
  useEffect(() => {
    addXpathToNodes(rawText);
  }, [rawText]);

  const editor = useEditor({
    isPreview,
    toggleVocabularySupport,
    keyTermDataContentId,
    levelId,
    rawText,
    focusAnnotationCard,
  });

  const updateEditorData = useDataTransactions({
    editor,
    currentFontSize,
    media,
    levelId,
    rawText,
    legacyArticleId,
    content,
    language,
    lexile,
  });

  useEffect(() => {
    if (editor && !isPreview) {
      insertChecksForUnderstanding({
        editor,
        quizAttachmentData: checksForUnderstandingQuiz,
        contentId: content?.id,
        assignmentId,
        levelId,
        onInsertionComplete: updateEditorData,
      });
    }
  }, [
    editor,
    JSON.stringify(checksForUnderstandingQuiz),
    levelId,
    assignmentId,
    isPreview,
  ]);

  if (!rawText || !editor) return null;

  return (
    <div className="editorWrapper" data-testid="editorWrapper">
      {!isPreview && <TextSelectionMenu editor={editor} />}
      <ReadOnlyEditorContent
        editor={editor}
        data-testid="readOnlyEditorContent"
      />
    </div>
  );
};
